import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import Select from "react-select";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection, {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import { logout } from "../../app/auth/authSlice.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from '../../hooks/useThemeColor';
import "./ManageCompany.css";

const ManageCompany = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Theme hooks
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState({
    value: "CompID",
    label: "Company ID",
  });
  const [sortOrder, setSortOrder] = useState({ value: "DESC", label: "Desc" });
  const [companyType, setCompanyType] = useState({
    value: "",
    label: "All Types",
  });
  const [paymentFilter, setPaymentFilter] = useState({
    value: "",
    label: "All billing",
  });
  const [productFilter, setProductFilter] = useState({
    value: "",
    label: "All products",
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedParents, setExpandedParents] = useState({});
  const [branchesByParent, setBranchesByParent] = useState({});
  const [loadingBranchesFor, setLoadingBranchesFor] = useState(null);

  const sortByOptions = [
    { value: "CompID", label: "Company ID" },
    { value: "CompName", label: "Company Name" },
    { value: "CompEmail", label: "Email" },
    { value: "CompPhone", label: "Phone" },
    { value: "CompIndustryType", label: "Industry Type" },
    { value: "CompCity", label: "City" },
    { value: "CompCurrency", label: "Currency" },
  ];

  const companyTypeOptions = [
    { value: "", label: "All Types" },
    { value: "Tech", label: "Technology" },
    { value: "Finance", label: "Finance" },
    { value: "Healthcare", label: "Healthcare" },
    { value: "Retail", label: "Retail" },
    { value: "Manufacturing", label: "Manufacturing" },
  ];

  const paymentFilterOptions = [
    { value: "", label: "All billing" },
    { value: "active", label: "Paying (active)" },
    { value: "expired", label: "Not paying (expired)" },
    { value: "expiring", label: "Expiring in 30 days" },
    { value: "has_due", label: "Has unpaid invoices" },
  ];

  const productFilterOptions = [
    { value: "", label: "All products" },
    { value: "pos", label: "POS only" },
    { value: "gofatafat", label: "Go Fatafat only" },
    { value: "both", label: "POS + Go Fatafat" },
  ];

  const FetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_COMPANIES, {
        params: {
          type: companyType.value,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search.trim() || "",
          PageSize: pageSize.value,
          PageNumber: pageNumber,
          onlyParentCompanies: true,
          paymentFilter: paymentFilter.value,
          productFilter: productFilter.value,
        },
      });

      if (response.data.status) {
        const companyData = response.data.data.companies;

        if (Array.isArray(companyData)) {
          setCompanies(companyData);
          // Use response totalRecords, or fall back to data.totalRecords, or calculate from data length
          const totalRecs = response.data.totalRecords || response.data.data.totalRecords || companyData.length;
          const totalPgs = response.data.totalPages || response.data.data.totalPages || Math.ceil(totalRecs / parseInt(pageSize.value));

          console.log("API Response Debug:", {
            totalRecords: response.data.totalRecords,
            dataTotalRecords: response.data.data?.totalRecords,
            calculatedTotal: totalRecs,
            totalPages: totalPgs,
            companiesLength: companyData.length,
            pageSize: pageSize.value
          });

          setTotalRecords(totalRecs);
          setTotalPages(totalPgs);
        } else {
          console.error("Unexpected API response format:", response.data);
          setCompanies([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
      } else {
        showErrorToast(response.data.Message || "Failed to fetch companies");
        setCompanies([]);
        setTotalRecords(0);
        setTotalPages(1);
      }

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 0);
      } else {
        showErrorToast("Failed to fetch companies. Please try again.");
        console.error("Error fetching companies:", err);
      }
    }
  }, [
    search,
    pageSize.value,
    sortBy.value,
    sortOrder.value,
    companyType.value,
    paymentFilter.value,
    productFilter.value,
    pageNumber,
  ]);

  const fetchBranches = useCallback(async (parentCompID) => {
    if (branchesByParent[parentCompID] !== undefined) return;
    try {
      setLoadingBranchesFor(parentCompID);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID: parentCompID },
      });
      if (response.data?.status && Array.isArray(response.data?.data)) {
        const raw = response.data.data;
        const parentId = Number(parentCompID);
        const list = raw.filter((b) => Number(b?.compID ?? b?.CompID ?? 0) !== parentId);
        setBranchesByParent((prev) => ({ ...prev, [parentCompID]: list }));
        if (list.length === 0) {
          setExpandedParents((prev) => ({ ...prev, [parentCompID]: false }));
        }
      } else {
        setBranchesByParent((prev) => ({ ...prev, [parentCompID]: [] }));
        setExpandedParents((prev) => ({ ...prev, [parentCompID]: false }));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 0);
      } else {
        showErrorToast("Failed to load branches.");
      }
      setBranchesByParent((prev) => ({ ...prev, [parentCompID]: [] }));
      setExpandedParents((prev) => ({ ...prev, [parentCompID]: false }));
    } finally {
      setLoadingBranchesFor(null);
    }
  }, [branchesByParent, dispatch, navigate]);

  const toggleExpand = useCallback(async (parentCompID) => {
    const isExpanded = expandedParents[parentCompID];
    if (isExpanded) {
      setExpandedParents((prev) => ({ ...prev, [parentCompID]: false }));
      return;
    }
    setExpandedParents((prev) => ({ ...prev, [parentCompID]: true }));
    await fetchBranches(parentCompID);
  }, [expandedParents, fetchBranches]);

  const companyField = (c, key) => c?.[key] ?? c?.[key.charAt(0).toUpperCase() + key.slice(1)] ?? "N/A";

  const getExpireDate = (company) => {
    const raw = companyField(company, "compExpireDate");
    if (!raw || raw === "N/A") return null;
    const parsed = moment(raw);
    return parsed.isValid() ? parsed : null;
  };

  const getOutstandingBalance = (company) =>
    parseFloat(company?.outstandingBalance ?? company?.OutstandingBalance ?? 0) || 0;

  const getUnpaidInvoiceCount = (company) =>
    parseInt(company?.unpaidInvoiceCount ?? company?.UnpaidInvoiceCount ?? 0, 10) || 0;

  const getBillingSummary = (company) => {
    const expire = getExpireDate(company);
    const outstanding = getOutstandingBalance(company);
    const unpaidCount = getUnpaidInvoiceCount(company);
    const isActive = expire ? expire.isSameOrAfter(moment(), "day") : false;
    const daysLeft = expire ? expire.diff(moment(), "days") : null;
    return { expire, outstanding, unpaidCount, isActive, daysLeft };
  };

  const renderPackageCell = (co, isBranch = false, parentCompany = null) => {
    const name = isBranch
      ? companyField(parentCompany, "packageName")
      : companyField(co, "packageName");
    if (name && name !== "N/A" && String(name).trim() !== "") {
      return (
        <span className={`badge ${isBranch ? "bg-secondary" : "bg-info"}`} title={isBranch ? "Parent package" : undefined}>
          {name}
        </span>
      );
    }
    return <span className="text-muted small">{isBranch ? "—" : "None"}</span>;
  };

  const renderBillingStatus = (company, isBranch = false) => {
    if (isBranch) {
      return <span className="text-muted small">—</span>;
    }

    const { expire, outstanding, unpaidCount, isActive, daysLeft } = getBillingSummary(company);
    const currency = companyField(company, "compCurrency");

    return (
      <div className="billing-status-cell">
        <div className="billing-status-badge">
          {isActive ? (
            <span className="badge bg-success">Paying</span>
          ) : (
            <span className="badge bg-danger">Not paying</span>
          )}
        </div>
        {expire && (
          <div className="billing-detail text-muted">
            {isActive
              ? `Until ${expire.format("MMM D, YYYY")}${daysLeft !== null ? ` (${daysLeft}d)` : ""}`
              : `Expired ${expire.format("MMM D, YYYY")}`}
          </div>
        )}
        {outstanding > 0 && (
          <div className="billing-detail text-warning fw-semibold">
            Due: {currency} {outstanding.toFixed(2)}
            {unpaidCount > 0 ? ` · ${unpaidCount} invoice${unpaidCount > 1 ? "s" : ""}` : ""}
          </div>
        )}
        {isActive && outstanding <= 0 && (
          <div className="billing-detail text-success">No balance due</div>
        )}
      </div>
    );
  };

  const getCompanyId = (c) => {
    const raw = companyField(c, "compID");
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : 0;
  };

  const getCompanyName = (c) => {
    const name = companyField(c, "compName");
    return name === "N/A" ? "this company" : name;
  };

  const DeleteCompany = async (company) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${company.compName || "this company"
        }?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.delete(APIs.DELETE_COMPANY, {
        params: {
          CompID: company.compID,
        },
      });

      if (response.data.status) {
        setCompanies((prevCompanies) =>
          prevCompanies.filter(
            (delCompany) => delCompany.compID !== company.compID
          )
        );
        showSuccessToast("Company is Deleted.");

        if (companies.length === 1 && pageNumber > 1) {
          setPageNumber((prev) => prev - 1);
        }
      } else {
        showErrorToast(response.data.message || "Failed to delete company");
      }
    } catch (err) {
      showErrorToast("Something went wrong while deleting company.");
      console.error("Error deleting company:", err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-Login Handler
  const handleAutoLogin = async (company) => {
    const compID = getCompanyId(company);
    const compName = getCompanyName(company);

    if (!compID) {
      showErrorToast("Invalid company ID. Cannot start auto-login.");
      return;
    }

    const confirmLogin = await swal({
      title: "Auto-Login Confirmation",
      text: `Do you want to login to ${compName}'s BackOffice system?`,
      icon: "info",
      buttons: {
        cancel: {
          text: "Cancel",
          value: false,
          visible: true,
          className: "btn-secondary",
        },
        confirm: {
          text: "Yes, Login",
          value: true,
          visible: true,
          className: "btn-primary",
        },
      },
    });

    if (!confirmLogin) {
      return;
    }

    try {
      setLoading(true);

      // Call API to generate auto-login token
      const response = await axiosInstance.post(APIs.GENERATE_AUTO_LOGIN_TOKEN, {
        CompID: compID,
      });

      const responseData = response.data?.data ?? response.data?.Data;
      const token = responseData?.token ?? responseData?.Token;

      const isSuccess = response.data?.status ?? response.data?.Status;
      if (isSuccess && token) {

        // Get the BackOffice URL - adjust this based on your environment
        const backOfficeUrl = process.env.REACT_APP_BACKOFFICE_URL || 'http://localhost:3001/';

        // Construct the auto-login URL with token
        const autoLoginUrl = `${backOfficeUrl}auto-login?token=${encodeURIComponent(token)}&compId=${compID}`;

        // Open BackOffice in new tab
        window.open(autoLoginUrl, '_blank');

        showSuccessToast(`Auto-login initiated for ${compName}`);
      } else {
        showErrorToast(response.data.message || "Failed to generate auto-login token");
      }
    } catch (err) {
      console.error("Auto-login error:", err);
      if (err.response?.status === 401) {
        showErrorToast("Unauthorized. Please login again.");
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast(
          err.response?.data?.message ||
          "Failed to initiate auto-login. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (compID) => {
    navigate(`/company/updateCompany/${compID}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPageNumber(1);
  };

  useEffect(() => {
    setPageNumber(1);
  }, [
    search,
    pageSize.value,
    sortBy.value,
    sortOrder.value,
    companyType.value,
    paymentFilter.value,
    productFilter.value,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      FetchCompanies();
    }, 500);

    return () => clearTimeout(handler);
  }, [FetchCompanies]);

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">ManageCompany</li>
          </ol>
          <h1 className="page-header mb-0">ManageCompany</h1>
        </div>

        <div className="ms-auto">
          <Link to="/company/addCompany">
            <button
              className="btn px-4"
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add Company
            </button>
          </Link>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            {/* Enhanced Filter Section */}
            <div className="mb-4">
              <div
                className="card filter-card"
                style={{
                  position: "relative",
                  zIndex: 1,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: `0 4px 20px ${themeColor}40`
                }}
              >
                <div className="card-body p-3">
                  <div className="row g-3 align-items-center">
                    {/* Search Field */}
                    <div className="col-xl-3 col-lg-3 col-md-6">
                      <div className="search-input filter-control">
                        <input
                          type="text"
                          className="filter-input form-control"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search companies..."
                          style={{
                            backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.3)',
                            color: isDarkMode ? '#ffffff' : '#333',
                            borderRadius: '6px',
                            height: '36px'
                          }}
                        />
                      </div>
                    </div>

                    {/* Company Type Dropdown */}
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={companyType}
                          onChange={setCompanyType}
                          options={companyTypeOptions}
                          styles={dropdownStyles}
                          placeholder="All Types"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuShouldBlockScroll={false}
                          menuShouldScrollIntoView={false}
                          closeMenuOnScroll={false}
                        />
                      </div>
                    </div>

                    {/* Billing / Payment Filter */}
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={paymentFilter}
                          onChange={setPaymentFilter}
                          options={paymentFilterOptions}
                          styles={dropdownStyles}
                          placeholder="All billing"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuShouldBlockScroll={false}
                          menuShouldScrollIntoView={false}
                          closeMenuOnScroll={false}
                        />
                      </div>
                    </div>

                    {/* Product: POS / Go Fatafat */}
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={productFilter}
                          onChange={setProductFilter}
                          options={productFilterOptions}
                          styles={dropdownStyles}
                          placeholder="All products"
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuShouldBlockScroll={false}
                          menuShouldScrollIntoView={false}
                          closeMenuOnScroll={false}
                        />
                      </div>
                    </div>

                    {/* Page Size Dropdown */}
                    <div className="col-xl-1 col-lg-1 col-md-6">
                      <div className="filter-control">
                        <PageSizeDropDownSelection
                          defaultValue={pageSize}
                          onChange={setPageSize}
                          styles={dropdownStyles}
                        />
                      </div>
                    </div>

                    {/* Sort By Dropdown */}
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={sortBy}
                          onChange={setSortBy}
                          options={sortByOptions}
                          styles={dropdownStyles}
                          placeholder="Sort by..."
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuShouldBlockScroll={false}
                          menuShouldScrollIntoView={false}
                          closeMenuOnScroll={false}
                        />
                      </div>
                    </div>

                    {/* Sort Order Dropdown */}
                    <div className="col-xl-1 col-lg-1 col-md-6">
                      <div className="filter-control">
                        <SortOrderDropDownSelection
                          defaultValue={sortOrder}
                          onChange={setSortOrder}
                          styles={dropdownStyles}
                        />
                      </div>
                    </div>

                    {/* Clear Filter Button */}
                    <div className="col-xl-2 col-lg-2 col-md-12">
                      <div className="d-flex gap-2">
                        <button
                          className="btn"
                          onClick={() => {
                            setSearch("");
                            setCompanyType({ value: "", label: "All Types" });
                            setPaymentFilter({ value: "", label: "All billing" });
                            setProductFilter({ value: "", label: "All products" });
                          }}
                          title="Clear all filters"
                          style={{
                            height: "36px",
                            backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.3)',
                            color: 'white',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fa fa-times me-1"></i>
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="table-responsive table-container"
              style={{
                minHeight: "200px",
                maxHeight: "450px",
                overflowY: "auto",
                overflowX: "auto",
                position: "relative",
                zIndex: 1
              }}
            >
              <table className="table table-hover text-nowrap" style={{ position: "relative" }}>
                <thead
                  className="sticky-header"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10000,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th className="pt-2 pb-2" style={{ width: "40px" }}></th>
                    <th className="pt-2 pb-2">ID</th>
                    <th className="pt-2 pb-2">Name</th>
                    <th className="pt-2 pb-2">Email</th>
                    <th className="pt-2 pb-2">Phone</th>
                    <th className="pt-2 pb-2">Type</th>
                    <th className="pt-2 pb-2">City</th>
                    <th className="pt-2 pb-2">Currency</th>
                    <th className="pt-2 pb-2" style={{ minWidth: "120px" }}>Package</th>
                    <th className="pt-2 pb-2" style={{ minWidth: "160px" }}>Billing</th>
                    <th className="pt-2 pb-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.length > 0 ? (
                    companies.map((company, index) => {
                      const compID = companyField(company, "compID");
                      const isExpanded = !!expandedParents[compID];
                      const branches = branchesByParent[compID] ?? [];
                      const isLoadingBranches = loadingBranchesFor === compID;
                      const hasBranches = branchesByParent[compID] === undefined || branches.length > 0;
                      return (
                        <React.Fragment key={`parent-${compID}-${index}`}>
                          <tr>
                            <td className="align-middle" style={{ width: "40px", verticalAlign: "middle" }}>
                              {hasBranches && (
                                <button
                                  type="button"
                                  className="btn btn-sm p-0 border-0 bg-transparent"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(compID);
                                  }}
                                  title={isExpanded ? "Collapse branches" : "Expand to load branches"}
                                  style={{ color: isDarkMode ? "#fff" : "#333" }}
                                >
                                  {isLoadingBranches ? (
                                    <i className="fa fa-spinner fa-spin" style={{ fontSize: "14px" }} />
                                  ) : (
                                    <i className={`fa fa-chevron-${isExpanded ? "down" : "right"}`} style={{ fontSize: "12px" }} />
                                  )}
                                </button>
                              )}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {companyField(company, "compID")}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {companyField(company, "compName")}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {companyField(company, "compEmail")}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {companyField(company, "compPhone")}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {companyField(company, "compIndustryType")}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {companyField(company, "compCity")}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {companyField(company, "compCurrency")}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to view subscription"
                            >
                              {renderPackageCell(company)}
                            </td>
                            <td
                              className="align-middle billing-status-td"
                              onClick={() => handleRowClick(compID)}
                              title="Click to view billing"
                            >
                              {renderBillingStatus(company)}
                            </td>
                            <td className="align-middle text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleAutoLogin(company); }}
                                  className="btn btn-sm btn-outline-success"
                                  title="Auto-Login to BackOffice"
                                  disabled={loading}
                                  style={{
                                    width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center",
                                    borderRadius: "6px", border: "1px solid #28a745", color: "#28a745", transition: "all 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#28a745";
                                    e.currentTarget.style.color = "white";
                                    e.currentTarget.style.transform = "scale(1.05)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                    e.currentTarget.style.color = "#28a745";
                                    e.currentTarget.style.transform = "scale(1)";
                                  }}
                                >
                                  <i className="fa fa-sign-in-alt" style={{ fontSize: "12px" }} />
                                </button>
                                <Link
                                  to={`/company/updateCompany/${compID}`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Company"
                                  style={{
                                    width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center",
                                    borderRadius: "6px", border: `1px solid ${themeColor}`, color: themeColor, transition: "all 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = "white";
                                    e.currentTarget.style.transform = "scale(1.05)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = "scale(1)";
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: "12px" }} />
                                </Link>
                                <button
                                  onClick={(e) => { e.stopPropagation(); DeleteCompany(company); }}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete Company"
                                  disabled={loading}
                                  style={{
                                    width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center",
                                    borderRadius: "6px", border: "1px solid #dc3545", color: "#dc3545", transition: "all 0.2s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#dc3545";
                                    e.currentTarget.style.color = "white";
                                    e.currentTarget.style.transform = "scale(1.05)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                    e.currentTarget.style.color = "#dc3545";
                                    e.currentTarget.style.transform = "scale(1)";
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && Array.isArray(branches) && branches.map((branch, bIdx) => {
                            const branchId = companyField(branch, "compID");
                            return (
                              <tr
                                key={`branch-${compID}-${branchId}-${bIdx}`}
                                style={{
                                  backgroundColor: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.04)",
                                }}
                              >
                                <td className="align-middle" style={{ width: "40px", paddingLeft: "28px" }}>
                                  <i className="fa fa-level-down-alt fa-rotate-90" style={{ fontSize: "11px", opacity: 0.7 }} title="Branch" />
                                </td>
                                <td className="align-middle" onClick={() => handleRowClick(branchId)} style={{ cursor: "pointer" }} title="Click to edit">
                                  {companyField(branch, "compID")}
                                </td>
                                <td className="align-middle" onClick={() => handleRowClick(branchId)} style={{ cursor: "pointer" }} title="Click to edit">
                                  {companyField(branch, "compName")}
                                </td>
                                <td className="align-middle" onClick={() => handleRowClick(branchId)} style={{ cursor: "pointer" }} title="Click to edit">
                                  {companyField(branch, "compEmail")}
                                </td>
                                <td className="align-middle" onClick={() => handleRowClick(branchId)} style={{ cursor: "pointer" }} title="Click to edit">
                                  {companyField(branch, "compPhone")}
                                </td>
                                <td className="align-middle" onClick={() => handleRowClick(branchId)} style={{ cursor: "pointer" }} title="Click to edit">
                                  {companyField(branch, "compIndustryType")}
                                </td>
                                <td className="align-middle" onClick={() => handleRowClick(branchId)} style={{ cursor: "pointer" }} title="Click to edit">
                                  {companyField(branch, "compCity")}
                                </td>
                                <td className="align-middle" onClick={() => handleRowClick(branchId)} style={{ cursor: "pointer" }} title="Click to edit">
                                  {companyField(branch, "compCurrency")}
                                </td>
                                <td className="align-middle">{renderPackageCell(branch, true, company)}</td>
                                <td className="align-middle">{renderBillingStatus(branch, true)}</td>
                                <td className="align-middle text-center">
                                  <div className="d-flex justify-content-center gap-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleAutoLogin(branch); }}
                                      className="btn btn-sm btn-outline-success"
                                      title="Auto-Login to BackOffice"
                                      disabled={loading}
                                      style={{
                                        width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center",
                                        borderRadius: "6px", border: "1px solid #28a745", color: "#28a745",
                                      }}
                                    >
                                      <i className="fa fa-sign-in-alt" style={{ fontSize: "12px" }} />
                                    </button>
                                    <Link
                                      to={`/company/updateCompany/${branchId}`}
                                      className="btn btn-sm btn-outline-primary"
                                      title="Edit Branch"
                                      style={{
                                        width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center",
                                        borderRadius: "6px", border: `1px solid ${themeColor}`, color: themeColor,
                                      }}
                                    >
                                      <i className="fa fa-edit" style={{ fontSize: "12px" }} />
                                    </Link>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); DeleteCompany(branch); }}
                                      className="btn btn-sm btn-outline-danger"
                                      title="Delete Branch"
                                      disabled={loading}
                                      style={{
                                        width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center",
                                        borderRadius: "6px", border: "1px solid #dc3545", color: "#dc3545",
                                      }}
                                    >
                                      <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {isExpanded && Array.isArray(branches) && branches.length === 0 && !isLoadingBranches && (
                            <tr style={{ backgroundColor: isDarkMode ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.03)" }}>
                              <td colSpan={12} className="align-middle text-muted" style={{ paddingLeft: "48px", fontSize: "13px" }}>
                                No branches for this company.
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={12}
                        style={{ textAlign: "center", fontSize: "18px" }}
                        className="align-middle"
                      >
                        {loading
                          ? "Loading companies..."
                          : "No companies found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalRecords={totalRecords > 0 ? totalRecords : companies.length}
              pageSize={parseInt(pageSize.value) || 50}
              pageNumber={pageNumber}
              totalPages={totalPages > 0 ? totalPages : 1}
              setPageNumber={setPageNumber}
              setPageSize={setPageSize}
              themeColor={themeColor}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCompany;
